import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Label, PriceEdit } from '@hero/web-components';
import { cn } from '@hero/web-lib';
import { useOrgProfileApi } from '@hero/api-hooks';
import { TERM_DURATION_OPTIONS } from '../../mocks';
import {
  CarrierBankingDrawer,
  CarrierLegalDrawer,
  FinancingProfileRequirementCard,
  RequirementsCard,
  carrierBankingChecks,
  carrierLegalChecks,
  isCarrierBankingComplete,
  isCarrierLegalComplete,
} from '../Requirements';
import type { ApplyFormValues } from './types';
import { LendersFieldset } from './LendersFieldset';

export type TermsFieldsetProps = {
  values: ApplyFormValues;
  onChange: <K extends keyof ApplyFormValues>(key: K, value: ApplyFormValues[K]) => void;
  onFinancingProfileSaved?: () => void;
};

/**
 * Stable mapping between `TermDurationOption` and the snake-case
 * key we persist in the form / submit payload. Lives at module
 * scope so renderers can iterate it without re-running the
 * `unit`/`value` ladder on every render.
 */
const TERM_KEYS = [
  '30_days',
  '60_days',
  '90_days',
  '6_months',
  '1_year',
] as const;
type TermKey = (typeof TERM_KEYS)[number];

const RECOMMENDED_TERM: TermKey = '60_days';

/**
 * Derive the current invoice amount for the "same as invoice" lock.
 * Both tabs already snapshot the invoice amount into the form
 * (`heroInvoiceAmount` set by `InvoiceFieldset.onHeroInvoicePick`,
 * `externalAmount` typed in by the carrier) so we just read it.
 * Returns `''` when no value is available so the bound `PriceEdit`
 * shows its placeholder instead of `0`.
 */
function invoiceAmountFromValues(values: ApplyFormValues): string {
  return values.invoiceTab === 'hero' ? values.heroInvoiceAmount : values.externalAmount;
}

export function TermsFieldset({
  values,
  onChange,
  onFinancingProfileSaved,
}: TermsFieldsetProps) {
  const { t } = useTranslation('m-financing');

  /**
   * Live snapshot of the carrier's profile (legal + bank fields).
   * Powers the requirement cards' status badges and feeds both
   * focused drawers their initial values. We let TanStack cache this
   * — the api-hook layer invalidates on profile-update mutations so
   * the cards re-tick automatically after a drawer save.
   */
  const profileQuery = useOrgProfileApi();
  const profile = profileQuery.data;

  const [isLegalOpen, setIsLegalOpen] = useState(false);
  const [isBankingOpen, setIsBankingOpen] = useState(false);

  // Derived per-field check results — converted into short labels
  // for the `missingItems` list shown on the card.
  const legalChecks = carrierLegalChecks(profile);
  const bankingChecks = carrierBankingChecks(profile);
  const legalMissing = [
    !legalChecks.registrationName &&
      t('apply_form.requirements.carrier_legal.missing.registration_name'),
    !legalChecks.registrationId &&
      t('apply_form.requirements.carrier_legal.missing.registration_id'),
    !legalChecks.registrationImage &&
      t('apply_form.requirements.carrier_legal.missing.registration_image'),
    !legalChecks.taxId && t('apply_form.requirements.carrier_legal.missing.tax_id'),
    !legalChecks.taxImage &&
      t('apply_form.requirements.carrier_legal.missing.tax_image'),
  ].filter(Boolean) as string[];
  const bankingMissing = [
    !bankingChecks.bankName &&
      t('apply_form.requirements.carrier_banking.missing.bank_name'),
    !bankingChecks.bankIBAN &&
      t('apply_form.requirements.carrier_banking.missing.bank_iban'),
    !bankingChecks.bankAccountName &&
      t('apply_form.requirements.carrier_banking.missing.bank_account_name'),
    !bankingChecks.bankAccountImage &&
      t('apply_form.requirements.carrier_banking.missing.bank_account_image'),
  ].filter(Boolean) as string[];

  const lockedAmount = invoiceAmountFromValues(values);
  const isLocked = values.requestedAmountSameAsInvoice;
  /**
   * When locked, render the invoice amount (read from the source of
   * truth, not from `values.requestedAmount`). Keeps the UI honest
   * even if the form state momentarily drifts (e.g. the user typed
   * a custom amount then ticked the toggle back on).
   */
  const displayedAmount = isLocked ? lockedAmount : values.requestedAmount;

  const toggleSameAs = (next: boolean) => {
    onChange('requestedAmountSameAsInvoice', next);
    if (next) onChange('requestedAmount', lockedAmount);
  };

  return (
    <fieldset className="space-y-5">
      <legend className="mb-1">
        <h3 className="text-base font-semibold text-gray-900">
          {t('apply_form.terms.title')}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {t('apply_form.terms.description')}
        </p>
      </legend>

      {/*
       * Requirement gates — financials & profile first, then carrier
       * legal + banking, then the advance amount / term pickers.
       */}
      <div className="space-y-2">
        <FinancingProfileRequirementCard
          onChange={onChange}
          onProfileSaved={onFinancingProfileSaved}
        />
        <RequirementsCard
          title={t('apply_form.requirements.carrier_legal.title')}
          description={t('apply_form.requirements.carrier_legal.description')}
          complete={isCarrierLegalComplete(profile)}
          level="hard"
          actionLabel={t('apply_form.requirements.carrier_legal.action')}
          onAction={() => setIsLegalOpen(true)}
          missingItems={legalMissing}
          disabled={profileQuery.isLoading}
        />
        <RequirementsCard
          title={t('apply_form.requirements.carrier_banking.title')}
          description={t('apply_form.requirements.carrier_banking.description')}
          complete={isCarrierBankingComplete(profile)}
          level="hard"
          actionLabel={t('apply_form.requirements.carrier_banking.action')}
          onAction={() => setIsBankingOpen(true)}
          missingItems={bankingMissing}
          disabled={profileQuery.isLoading}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-end justify-between gap-3">
          <Label htmlFor="requested-amount">
            {t('apply_form.terms.requested_amount.title')}
            <span className="text-error ms-0.5">*</span>
          </Label>
          <Label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-600">
            <Checkbox
              checked={isLocked}
              onCheckedChange={v => toggleSameAs(v === true)}
            />
            {t('apply_form.terms.requested_amount.same_as_invoice')}
          </Label>
        </div>
        <PriceEdit
          name="requested-amount"
          value={displayedAmount === '' ? undefined : Number(displayedAmount)}
          onChange={v => onChange('requestedAmount', v == null ? '' : String(v))}
          disabled={isLocked}
          placeholder={t('apply_form.terms.requested_amount.placeholder')}
        />
        <p className="text-xs text-gray-500">
          {isLocked
            ? t('apply_form.terms.requested_amount.locked_hint')
            : t('apply_form.terms.requested_amount.hint')}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label>
          {t('apply_form.terms.requested_term.title')}
          <span className="text-error ms-0.5">*</span>
        </Label>
        <p className="text-xs text-gray-500">
          {t('apply_form.terms.requested_term.description')}
        </p>
        {/*
         * Selectable cards instead of a dropdown. Three reasons:
         *  - each term has its own meaning (cashflow timing, fees,
         *    underwriting profile) so we want a short blurb visible
         *    at a glance, not hidden behind a select trigger.
         *  - the carrier almost always picks 60 days — surfacing
         *    "Recommended" on it nudges the right default.
         *  - the chip layout works on narrow viewports without the
         *    select popup overlap that breaks on small phones.
         */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {TERM_KEYS.map(key => {
            const isSelected = values.requestedTermKey === key;
            const isRecommended = key === RECOMMENDED_TERM;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onChange('requestedTermKey', key)}
                aria-pressed={isSelected}
                className={cn(
                  'group flex flex-col items-start gap-1 rounded-xl border p-3 text-start transition-all',
                  isSelected
                    ? 'border-emerald-300 bg-emerald-50/40 shadow-sm ring-2 ring-emerald-200'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/60',
                )}>
                <div className="flex w-full items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">
                    {t(`term.${key}`)}
                  </span>
                  {isRecommended && (
                    <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-medium text-white">
                      {t('apply_form.terms.requested_term.recommended_badge')}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {t(`apply_form.terms.requested_term.descriptions.${key}`)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <LendersFieldset />

      {/*
       * Drawers — mounted unconditionally so their open/close
       * animations run. The shared profile query above is the
       * single source of truth, so saving inside a drawer triggers
       * a TanStack invalidation that re-evaluates the cards on
       * close.
       */}
      <CarrierLegalDrawer
        isOpen={isLegalOpen}
        onClose={() => setIsLegalOpen(false)}
        profile={profile}
      />
      <CarrierBankingDrawer
        isOpen={isBankingOpen}
        onClose={() => setIsBankingOpen(false)}
        profile={profile}
      />
    </fieldset>
  );
}

/** Term-key constants exported for the parent's submit serialization. */
export { TERM_DURATION_OPTIONS };
